/**
 * Geodesy kept deliberately small: no turf dependency, because this code runs
 * in the browser, in a Cloudflare Worker and on the phone in a hot loop.
 */

import type { BBox, LngLat } from './types.js';

const EARTH_RADIUS_M = 6371008.8;
const DEG_TO_RAD = Math.PI / 180;

/** Great-circle distance in metres. */
export function haversineMeters(a: LngLat, b: LngLat): number {
  const lat1 = a[1] * DEG_TO_RAD;
  const lat2 = b[1] * DEG_TO_RAD;
  const dLat = lat2 - lat1;
  const dLng = (b[0] - a[0]) * DEG_TO_RAD;
  const s =
    Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(s)));
}

/**
 * Metres per degree of longitude at a given latitude. Used to flatten a small
 * area into a local planar frame, where segment maths is both exact enough and
 * far cheaper than doing it on the sphere.
 */
export function metersPerDegreeLng(latDeg: number): number {
  return 111320 * Math.cos(latDeg * DEG_TO_RAD);
}

export const METERS_PER_DEGREE_LAT = 110574;

export interface NearestPointResult {
  /** Perpendicular distance from the query point to the segment, in metres. */
  distance: number;
  /** Closest point on the segment. */
  point: LngLat;
  /** Position along the segment, 0 at `a`, 1 at `b`. */
  t: number;
}

/**
 * Distance from a point to a line segment, computed in a local planar frame
 * centred on the query point. Accurate to well under a metre at the scales that
 * matter here, and about 20x cheaper than the spherical equivalent.
 */
export function pointToSegmentMeters(p: LngLat, a: LngLat, b: LngLat): NearestPointResult {
  const mPerLng = metersPerDegreeLng(p[1]);
  const px = 0;
  const py = 0;
  const ax = (a[0] - p[0]) * mPerLng;
  const ay = (a[1] - p[1]) * METERS_PER_DEGREE_LAT;
  const bx = (b[0] - p[0]) * mPerLng;
  const by = (b[1] - p[1]) * METERS_PER_DEGREE_LAT;

  const dx = bx - ax;
  const dy = by - ay;
  const lenSq = dx * dx + dy * dy;

  let t = 0;
  if (lenSq > 0) {
    t = ((px - ax) * dx + (py - ay) * dy) / lenSq;
    t = t < 0 ? 0 : t > 1 ? 1 : t;
  }

  const cx = ax + t * dx;
  const cy = ay + t * dy;
  const distance = Math.hypot(cx - px, cy - py);

  return {
    distance,
    t,
    point: [p[0] + cx / mPerLng, p[1] + cy / METERS_PER_DEGREE_LAT],
  };
}

export function bboxOf(coords: LngLat[]): BBox {
  let west = Infinity;
  let south = Infinity;
  let east = -Infinity;
  let north = -Infinity;
  for (const [lng, lat] of coords) {
    if (lng < west) west = lng;
    if (lng > east) east = lng;
    if (lat < south) south = lat;
    if (lat > north) north = lat;
  }
  return [west, south, east, north];
}

export function padBBox(box: BBox, meters: number): BBox {
  const midLat = (box[1] + box[3]) / 2;
  const dLat = meters / METERS_PER_DEGREE_LAT;
  const dLng = meters / Math.max(1, metersPerDegreeLng(midLat));
  return [box[0] - dLng, box[1] - dLat, box[2] + dLng, box[3] + dLat];
}

/** Cumulative distance along a path, in metres. Length === coords.length. */
export function cumulativeDistances(coords: LngLat[]): number[] {
  const cum = new Array<number>(coords.length);
  cum[0] = 0;
  for (let i = 1; i < coords.length; i++) {
    cum[i] = cum[i - 1]! + haversineMeters(coords[i - 1]!, coords[i]!);
  }
  return cum;
}

/**
 * Initial bearing from `a` to `b`, in degrees clockwise from north.
 *
 * Spherical rather than planar: over the short distances used here the
 * difference is negligible, but getting it right costs nothing and avoids a
 * surprise near the poles.
 */
export function bearingDegrees(a: LngLat, b: LngLat): number {
  const lat1 = a[1] * DEG_TO_RAD;
  const lat2 = b[1] * DEG_TO_RAD;
  const dLng = (b[0] - a[0]) * DEG_TO_RAD;

  const y = Math.sin(dLng) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);

  return (Math.atan2(y, x) / DEG_TO_RAD + 360) % 360;
}

/**
 * Signed difference between two bearings, in the range (-180, 180].
 *
 * Positive is clockwise. Used to draw a vehicle at the right angle on a rotated
 * map: the marker's rotation is the road's direction *relative to* whichever way
 * the map is currently facing.
 */
export function relativeBearing(from: number, to: number): number {
  let delta = (to - from) % 360;
  if (delta > 180) delta -= 360;
  if (delta <= -180) delta += 360;
  return delta;
}

/**
 * Blends two bearings, taking the short way round the compass.
 *
 * `t` of 0 gives `a`, 1 gives `b`. Interpolating the raw numbers would swing the
 * long way whenever the pair straddles north — 350° and 10° would pass through
 * 180° instead of through 0°.
 */
export function blendBearings(a: number, b: number, t: number): number {
  const clamped = t < 0 ? 0 : t > 1 ? 1 : t;
  return (((a + relativeBearing(a, b) * clamped) % 360) + 360) % 360;
}
